import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import ConnectionConfigModal from "../components/connection-config-modal/ConnectionConfigModal";
import { DataSource, dataSources } from "../../data/models";
import {
  SnowflakeService,
  SnowflakeConfigRequest
} from "../../data/services/snowflake.service";
import { MainRoutes } from "common/config/route.config";
import "./SnowflakeDataViewer.scss";

interface QueryResult {
  columns: string[];
  rows: Record<string, unknown>[];
  count: number;
  error?: string;
}

const SnowflakeDataViewer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const connectionName = searchParams.get("connection") || "Snowflake";

  // Connection config modal state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const snowflakeSource =
    dataSources.find((ds) => ds.type === "snowflake") || null;

  // Tables state
  const [tables, setTables] = useState<string[]>([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tablesError, setTablesError] = useState("");

  const fetchTables = useCallback(async () => {
    setTablesLoading(true);
    setTablesError("");
    try {
      const response = await SnowflakeService.getTables();
      if (response.data) {
        setTables(response.data.tables);
      } else {
        setTablesError("Failed to load tables.");
      }
    } catch (error) {
      setTablesError("Failed to load tables.");
    } finally {
      setTablesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // Browse table state
  const [selectedTable, setSelectedTable] = useState("");
  const [rowLimit, setRowLimit] = useState(10);
  const [browseLoading, setBrowseLoading] = useState(false);

  // Custom query state
  const [customQuery, setCustomQuery] = useState("");
  const [queryLoading, setQueryLoading] = useState(false);

  // Results state
  const [results, setResults] = useState<QueryResult | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const handleBack = () => {
    navigate(MainRoutes.DATA_INTEGRATION_HUB);
  };

  const handleConfigureConnection = () => {
    setShowConfigModal(true);
  };

  const handleCloseModal = () => {
    setShowConfigModal(false);
  };

  const handleSaveConnection = async (config: {
    name: string;
    config: Record<string, string>;
  }) => {
    const payload: SnowflakeConfigRequest = {
      user: config.config.username,
      password: config.config.password,
      account: config.config.account,
      warehouse: config.config.warehouse,
      database: config.config.database,
      db_schema: config.config.schema || "PUBLIC"
    };

    try {
      const response = await SnowflakeService.saveConfig(payload);
      if (response.error) {
        console.error("Failed to save Snowflake config:", response.error);
        setShowConfigModal(false);
        return;
      }
    } catch (error) {
      console.error("Error saving Snowflake config:", error);
      setShowConfigModal(false);
      return;
    }

    setShowConfigModal(false);
    fetchTables();
  };

  const handleSelectTable = (tableName: string) => {
    setSelectedTable(tableName);
  };

  const handleLoadData = useCallback(async () => {
    if (!selectedTable) {
      setErrorMsg("Please select a table.");
      return;
    }
    setErrorMsg("");
    setResults(null);
    setBrowseLoading(true);
    try {
      const response = await SnowflakeService.getTableData(
        selectedTable,
        rowLimit
      );
      if (response.data) {
        setResults({
          columns: response.data.columns,
          rows: response.data.rows,
          count: response.data.count
        });
      } else {
        setErrorMsg("Failed to load table data.");
      }
    } catch (error) {
      setErrorMsg("Failed to load table data.");
    } finally {
      setBrowseLoading(false);
    }
  }, [selectedTable, rowLimit]);

  const handleRunQuery = useCallback(async () => {
    if (!customQuery.trim()) {
      setErrorMsg("Please enter a SQL query.");
      return;
    }
    setErrorMsg("");
    setResults(null);
    setQueryLoading(true);
    try {
      const response = await SnowflakeService.runQuery(customQuery);
      if (response.data) {
        setResults({
          columns: response.data.columns,
          rows: response.data.rows,
          count: response.data.count
        });
      } else {
        setErrorMsg("Failed to run query.");
      }
    } catch (error) {
      setErrorMsg("Failed to run query.");
    } finally {
      setQueryLoading(false);
    }
  }, [customQuery]);

  const renderResults = () => {
    if (!results) return null;

    if (results.error) {
      return <div className="error-message">{results.error}</div>;
    }

    return (
      <>
        <div className="results-stats">{results.count} row(s) returned</div>
        <div className="table-wrapper">
          <table className="results-table">
            <thead>
              <tr>
                {results.columns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {results.columns.map((col) => (
                    <td key={col}>
                      {row[col] === null ? "NULL" : String(row[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="snowflake-data-viewer">
        <HubHeader
          title="Snowflake"
          description="Browse and query your Snowflake tables"
          showBackButton={true}
          backRoute="/data-integration-hub"
        />
        <div className="viewer-header">
          <div className="header-left">
            <div className="header-text">
              <h1>❄️ Snowflake Data Viewer</h1>
            </div>
          </div>
          <button
            className="configure-button"
            onClick={handleConfigureConnection}
          >
            <SettingsIcon fontSize="small" />
            Configure Connection
          </button>
        </div>

        <div className="viewer-content">
          {/* Available Tables */}
          <div className="card">
            <div className="card-header-row">
              <h2>Available Tables</h2>
              <button
                className="refresh-button"
                onClick={fetchTables}
                disabled={tablesLoading}
                title="Refresh tables"
              >
                <RefreshIcon
                  fontSize="small"
                  className={tablesLoading ? "spinning" : ""}
                />
              </button>
            </div>
            <div className="tables-list">
              {tablesLoading ? (
                <span className="loading-text">Loading tables...</span>
              ) : tablesError ? (
                <span className="error-text">{tablesError}</span>
              ) : tables.length === 0 ? (
                <em className="no-tables">
                  No tables found. Configure your connection to load tables.
                </em>
              ) : (
                tables.map((table) => (
                  <span
                    key={table}
                    className={`table-badge ${
                      selectedTable === table ? "active" : ""
                    }`}
                    onClick={() => handleSelectTable(table)}
                  >
                    {table}
                  </span>
                ))
              )}
            </div>
          </div>

          {/* Browse Table */}
          <div className="card">
            <h2>Browse Table</h2>
            <div className="controls-row">
              <select
                className="table-select"
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
              >
                <option value="">-- Select a table --</option>
                {tables.map((table) => (
                  <option key={table} value={table}>
                    {table}
                  </option>
                ))}
              </select>
              <input
                type="number"
                className="row-limit-input"
                value={rowLimit}
                min={1}
                max={5000}
                onChange={(e) => setRowLimit(Number(e.target.value))}
                title="Row limit"
              />
              <button className="action-button" onClick={handleLoadData}>
                {browseLoading ? <span className="spinner" /> : "Load Data"}
              </button>
            </div>
          </div>

          {/* Custom SQL Query */}
          <div className="card">
            <h2>Custom SQL Query</h2>
            <textarea
              className="query-textarea"
              placeholder="SELECT * FROM YOUR_TABLE LIMIT 50"
              value={customQuery}
              onChange={(e) => setCustomQuery(e.target.value)}
            />
            <div className="controls-row">
              <button className="action-button" onClick={handleRunQuery}>
                {queryLoading ? <span className="spinner" /> : "Run Query"}
              </button>
            </div>
          </div>

          {/* Results */}
          {(results || errorMsg) && (
            <div className="card">
              <h2>Results</h2>
              {errorMsg && <div className="error-message">{errorMsg}</div>}
              {renderResults()}
            </div>
          )}
        </div>
      </div>

      {/* Connection Config Modal */}
      <ConnectionConfigModal
        isOpen={showConfigModal}
        onClose={handleCloseModal}
        onSave={handleSaveConnection}
        dataSource={snowflakeSource}
      />
    </>
  );
};

export default SnowflakeDataViewer;
